import { useCallback, useEffect, useMemo } from "react";
import { useReactiveVar } from "@apollo/client";
import { menuLanguageVersionVar } from "@me-team/host/main/menuLanguageVersions/state";

interface UseMenuLanguageVersionParams {
    primaryLanguageCode: string | null | undefined;
    availableLanguageCodes: string[] | null | undefined;
    isLockedToPrimary?: boolean;
}

const normalizeCode = (value?: string | null) => value?.trim() || null;

export const useMenuLanguageVersion = ({
    primaryLanguageCode,
    availableLanguageCodes,
    isLockedToPrimary,
}: UseMenuLanguageVersionParams) => {
    const selectedLangVersionCode = useReactiveVar(menuLanguageVersionVar);

    const updateLanguageVersion = useCallback((languageCode: string | null) => {
        menuLanguageVersionVar(normalizeCode(languageCode));
    }, []);

    const normalizedPrimaryLanguageCode = useMemo(() => normalizeCode(primaryLanguageCode), [primaryLanguageCode]);

    const normalizedAvailableLanguageCodes = useMemo(() => {
        return (availableLanguageCodes ?? []).map((code) => code.trim()).filter(Boolean);
    }, [availableLanguageCodes]);

    const candidateLanguageCode = useMemo(() => {
        if (isLockedToPrimary && normalizedPrimaryLanguageCode) return normalizedPrimaryLanguageCode;

        return normalizeCode(selectedLangVersionCode) ?? normalizedPrimaryLanguageCode ?? "en";
    }, [isLockedToPrimary, normalizedPrimaryLanguageCode, selectedLangVersionCode]);

    const currentLanguageCode = useMemo(() => {
        if (normalizedAvailableLanguageCodes.length === 0) return candidateLanguageCode;

        return normalizedAvailableLanguageCodes.includes(candidateLanguageCode)
            ? candidateLanguageCode
            : (normalizedPrimaryLanguageCode ?? "en");
    }, [candidateLanguageCode, normalizedAvailableLanguageCodes, normalizedPrimaryLanguageCode]);

    useEffect(() => {
        if (!normalizedPrimaryLanguageCode) return;

        const selectedNormalized = normalizeCode(selectedLangVersionCode);
        const shouldForcePrimary = Boolean(isLockedToPrimary);
        const shouldInitializeToPrimary = !selectedNormalized;
        const selectedNotAllowed =
            Boolean(selectedNormalized) &&
            normalizedAvailableLanguageCodes.length > 0 &&
            !normalizedAvailableLanguageCodes.includes(selectedNormalized);

        if (shouldForcePrimary || shouldInitializeToPrimary || selectedNotAllowed) {
            updateLanguageVersion(normalizedPrimaryLanguageCode);
        }
    }, [
        isLockedToPrimary,
        normalizedAvailableLanguageCodes,
        normalizedPrimaryLanguageCode,
        selectedLangVersionCode,
        updateLanguageVersion,
    ]);

    return {
        selectedLangVersionCode,
        currentLanguageCode,
        setMenuLanguageVersion: updateLanguageVersion,
        primaryLanguageCode: normalizedPrimaryLanguageCode,
        availableLanguageCodes: normalizedAvailableLanguageCodes,
    };
};
